import { useState, useEffect } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Panoramica.css";
import Lf23Loader from "../../../components/Lf23Loader";

const Panoramica = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [data, setData] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [comboViewType, setComboViewType] = useState('trimestrale');

    const [chartFilter, setChartFilter] = useState(null);
    const [filteredData, setFilteredData] = useState(null);

    const [isFullscreenPie, setIsFullscreenPie] = useState(false);
    const [isFullscreenLine, setIsFullscreenLine] = useState(false);
    const [isFullscreenCombo, setIsFullscreenCombo] = useState(false);
    const [isFullscreenTarga, setIsFullscreenTarga] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenPie(false);
                setIsFullscreenLine(false);
                setIsFullscreenCombo(false);
                setIsFullscreenTarga(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                // Costruisco i parametri della URL a partire dai filtri selezionati
                const params = new URLSearchParams();
                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);
                if (filters?.targa?.length > 0)
                    params.append("targa", filters.targa.map(t => t.value).join(","));
                if (filters?.prodotto?.length > 0)
                    params.append("prodotto", filters.prodotto.map(p => p.value).join(","));
                if (filters?.distributore?.length > 0)
                    params.append("distributore", filters.distributore.map(d => d.value).join(","));
                if (filters?.cantiere?.length > 0)
                    params.append("cantiere", filters.cantiere.map(c => c.value).join(","));
                if (filters?.tipologia?.length > 0)
                    params.append("tipologia", filters.tipologia.map(t => t.value).join(","));
                if (filters?.settore?.length > 0)
                    params.append("settore", filters.settore.map(s => s.value).join(","));
                if (filters?.commessa?.length > 0)
                    params.append("commessa", filters.commessa.map(c => c.value).join(","));

                const response = await axios.get(
                    `${API_URL}/trans-automezzi/panoramica?${params.toString()}`
                );
                setData(response.data);
            } catch (error) {
                console.error("Errore caricamento dati panoramica:", error);
                setData(null);
            } finally {
                setDataLoading(false);
            }
        };

        if (filters) {
            setChartFilter(null);
            setFilteredData(null);
            fetchData();
        }
    }, [filters, API_URL]);

    // Fetch dati filtrati quando si clicca su un grafico
    useEffect(() => {
        if (!chartFilter || !filters) {
            setFilteredData(null);
            return;
        }

        const fetchFilteredData = async () => {
            try {
                const params = new URLSearchParams();
                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);
                if (chartFilter.type !== 'targa' && filters?.targa?.length > 0)
                    params.append("targa", filters.targa.map(t => t.value).join(","));
                if (chartFilter.type !== 'prodotto' && filters?.prodotto?.length > 0)
                    params.append("prodotto", filters.prodotto.map(p => p.value).join(","));
                if (chartFilter.type !== 'distributore' && filters?.distributore?.length > 0)
                    params.append("distributore", filters.distributore.map(d => d.value).join(","));
                if (filters?.cantiere?.length > 0)
                    params.append("cantiere", filters.cantiere.map(c => c.value).join(","));
                if (filters?.tipologia?.length > 0)
                    params.append("tipologia", filters.tipologia.map(t => t.value).join(","));

                params.append(chartFilter.type, chartFilter.value);

                const response = await axios.get(
                    `${API_URL}/trans-automezzi/panoramica?${params.toString()}`
                );
                setFilteredData(response.data);
            } catch (error) {
                console.error("Errore caricamento dati filtrati:", error);
                setFilteredData(null);
            }
        };

        fetchFilteredData();
    }, [chartFilter, filters, API_URL]);

    // Click handlers per i 4 grafici
    const handlePieClick = (params) => {
        if (chartFilter?.type === 'distributore' && chartFilter?.value === params.name) {
            setChartFilter(null);
        } else {
            setChartFilter({ type: 'distributore', value: params.name });
        }
    };

    const handleLineClick = (params) => {
        if (chartFilter?.type === 'distributore' && chartFilter?.value === params.seriesName) {
            setChartFilter(null);
        } else {
            setChartFilter({ type: 'distributore', value: params.seriesName });
        }
    };

    const handleComboClick = (params) => {
        if (params.seriesType === 'line') return;
        if (chartFilter?.type === 'prodotto' && chartFilter?.value === params.seriesName) {
            setChartFilter(null);
        } else {
            setChartFilter({ type: 'prodotto', value: params.seriesName });
        }
    };

    const handleTargaClick = (params) => {
        if (params.seriesType === 'line') return;
        if (chartFilter?.type === 'targa' && chartFilter?.value === params.name) {
            setChartFilter(null);
        } else {
            setChartFilter({ type: 'targa', value: params.name });
        }
    };

    const formatCompact = (value) => {
        const num = Math.abs(value);
        if (num >= 1000000) return (value / 1000000).toFixed(2).replace('.', ',') + 'Mln';
        if (num >= 1000) return (value / 1000).toFixed(2).replace('.', ',') + 'K';
        return value.toFixed(2).replace('.', ',');
    };

    const formatEuro = (value) => formatCompact(value) + ' €';

    // formattazione italiana
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    // CONFIGURAZIONE GRAFICO TORTA (donut moderno con gradienti)
    const getPieChartOption = () => {
        if (!data?.pieData) return {};

        const isSource = chartFilter?.type === 'distributore';
        const source = isSource ? data : (filteredData || data);

        const MODERN_PIE_GRADIENTS = [
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#60A5FA' }, { offset: 1, color: '#2563EB' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A78BFA' }, { offset: 1, color: '#7C3AED' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F472B6' }, { offset: 1, color: '#DB2777' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FBBF24' }, { offset: 1, color: '#D97706' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#34D399' }, { offset: 1, color: '#059669' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#22D3EE' }, { offset: 1, color: '#0891B2' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#818CF8' }, { offset: 1, color: '#4F46E5' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FB923C' }, { offset: 1, color: '#EA580C' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#2DD4BF' }, { offset: 1, color: '#0D9488' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#C084FC' }, { offset: 1, color: '#9333EA' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F87171' }, { offset: 1, color: '#DC2626' }] },
            { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A3E635' }, { offset: 1, color: '#65A30D' }] }
        ];

        const totaleEuro = source.pieData.reduce((sum, t) => sum + Number(t.value || 0), 0);
        const selectedValue = isSource
            ? Number(source.pieData.find(t => t.name === chartFilter.value)?.value || 0)
            : totaleEuro;
        const centerMainText = formatEuro(selectedValue);
        const centerSubText = isSource
            ? `${chartFilter.value} · ${totaleEuro > 0 ? ((selectedValue / totaleEuro) * 100).toFixed(1) : 0}%`
            : 'Totale spesa';

        return {
            color: MODERN_PIE_GRADIENTS,
            tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderColor: 'transparent',
                borderRadius: 10,
                padding: [10, 14],
                textStyle: { color: '#F8FAFC', fontSize: 12, fontWeight: 500 },
                extraCssText: 'box-shadow: 0 10px 25px rgba(0,0,0,0.25); backdrop-filter: blur(8px);',
                formatter: (params) => `
                    <div style="font-weight:700;font-size:13px;margin-bottom:4px;">${params.name}</div>
                    <div style="display:flex;align-items:center;gap:6px;">
                        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${typeof params.color === 'string' ? params.color : (params.color?.colorStops?.[0]?.color || '#3B82F6')};"></span>
                        <span>${formatEuro(params.value)} · ${params.percent}%</span>
                    </div>
                `
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                itemWidth: 10,
                itemHeight: 10,
                itemGap: 12,
                icon: 'circle',
                textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 },
                pageIconColor: '#3B82F6',
                pageTextStyle: { color: '#64748B' }
            },
            graphic: [
                {
                    type: 'text',
                    left: 'center',
                    top: '38%',
                    z: 10,
                    style: {
                        text: centerMainText,
                        fontSize: 24,
                        fontWeight: 700,
                        fill: '#0F172A',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                    }
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '50%',
                    z: 10,
                    style: {
                        text: centerSubText,
                        fontSize: 11,
                        fontWeight: 500,
                        fill: '#64748B',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                    }
                }
            ],
            series: [{
                name: 'Distributore',
                type: 'pie',
                radius: ['55%', '78%'],
                center: ['50%', '45%'],
                avoidLabelOverlap: true,
                padAngle: 2,
                itemStyle: {
                    borderRadius: 8,
                    borderColor: '#ffffff',
                    borderWidth: 3,
                    shadowBlur: 10,
                    shadowColor: 'rgba(15, 23, 42, 0.12)'
                },
                data: source.pieData.map(item => ({
                    ...item,
                    itemStyle: isSource
                        ? item.name === chartFilter.value
                            ? { opacity: 1, shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' }
                            : { opacity: 0.3 }
                        : {}
                })),
                label: {
                    show: true,
                    position: 'outside',
                    formatter: (params) => `{b|${params.name}}\n{c|${formatEuro(params.value)}} · {p|${params.percent}%}`,
                    rich: {
                        b: { color: '#1E293B', fontSize: 11, fontWeight: 600, lineHeight: 16 },
                        c: { color: '#475569', fontSize: 11, fontWeight: 500, lineHeight: 14 },
                        p: { color: '#3B82F6', fontSize: 11, fontWeight: 700, lineHeight: 14 }
                    }
                },
                labelLine: {
                    show: true,
                    length: 10,
                    length2: 12,
                    smooth: true,
                    lineStyle: { color: '#CBD5E1', width: 1 }
                },
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 25,
                        shadowColor: 'rgba(59, 130, 246, 0.45)'
                    },
                    label: { fontWeight: 700 }
                },
                animationType: 'scale',
                animationEasing: 'elasticOut',
                animationDuration: 900,
                animationDelay: (idx) => idx * 60
            }]
        };
    };

    // CONFIGURAZIONE GRAFICO LINEE
    const getLineChartOption = () => {
        const isSource = chartFilter?.type === 'distributore';
        const source = isSource ? data : (filteredData || data);
        if (!source?.lineData) return {};

        // Calcolo min e max dinamici dai dati
        const allValues = source.lineData.series.flatMap(s => s.data).filter(v => v != null);
        const dataMin = Math.min(...allValues);
        const dataMax = Math.max(...allValues);
        const padding = (dataMax - dataMin) * 0.1 || 0.05;
        const yMin = Math.floor((dataMin - padding) * 100) / 100;
        const yMax = Math.ceil((dataMax + padding) * 100) / 100;

        return {
            tooltip: {
                trigger: 'item'
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                top: 20,
            },
            labelLayout: {
                hideOverlap: true
            },
            dataZoom: [
                {
                    type: 'inside',
                    yAxisIndex: 0
                }
            ],
            grid: {
                left: '3%',
                right: '4%',
                bottom: "0",
                top: '20%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: source.lineData.periodi,
                axisLabel: {
                    fontSize: 10
                }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    formatter: (val) => formatNumber(val) + ' €'
                }
            },
            series: source.lineData.series.map(s => ({
                name: s.name,
                type: 'line',
                smooth: true,
                data: s.data,
                connectNulls: true,
                lineStyle: {
                    opacity: isSource && s.name !== chartFilter.value ? 0.15 : 1
                },
                itemStyle: {
                    opacity: isSource && s.name !== chartFilter.value ? 0.15 : 1
                },
                label: {
                    show: !(isSource && s.name !== chartFilter.value),
                    fontSize: 11,
                    fontWeight: 'bold',
                    backgroundColor: '#e9e7e7a0',
                    padding: [2, 4],
                    formatter: (params) => {
                        if (params.value == null) return '';
                        return formatNumber(params.value) + ' €';
                    }
                },
                emphasis: {
                    focus: 'series',
                    label: {
                        show: true,
                        fontSize: 14,
                        fontWeight: 'bold'
                    }
                },
                // label: {
                //     show: false
                // },
                // endLabel: {
                //     show: !(isSource && s.name !== chartFilter.value),
                //     formatter: (params) => {
                //         if (params.value == null) return '';
                //         return params.seriesName + ': ' + formatNumber(params.value) + ' €';
                //     },
                //     fontSize: 9
                // },
            }))
        };
    };

    // CONFIGURAZIONE GRAFICO COMBO
    const getStackedBarLineOption = () => {
        const isSource = chartFilter?.type === 'prodotto';
        const source = isSource ? data : (filteredData || data);

        if (!source?.stackedBarData?.[comboViewType]) return {};

        const sbd = source.stackedBarData[comboViewType];

        // creo una serie "bar" per ogni prodotto
        const barSeries = sbd.prodotti.map(prodotto => ({
            name: prodotto,
            type: 'bar',
            stack: 'volume',
            data: sbd.volumePerProdotto[prodotto],
            itemStyle: {
                opacity: isSource && prodotto !== chartFilter.value ? 0.3 : 1
            },
        }));

        // aggiungo la serie linea per il costo totale
        const lineSeries = {
            name: 'Costo Totale',
            type: 'line',
            yAxisIndex: 1,
            data: sbd.costoTotale,
            itemStyle: { color: '#ff6600' },
            lineStyle: { width: 2, type: 'dashed' },
        };

        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    let totaleVolume = 0;
                    let result = `<b>${params[0].axisValue}</b><br/>`;

                    params.forEach(p => {
                        if (p.seriesType === 'bar') {
                            totaleVolume += p.value || 0;
                        }
                        const valoreFormattato = p.seriesType === 'bar'
                            ? formatNumber(p.value)
                            : formatNumber(p.value) + ' €';
                        result += `${p.marker} ${p.seriesName}: ${valoreFormattato}<br/>`;
                    });

                    result += `<br/><b>Totale Volume: ${formatNumber(totaleVolume)}</b>`;
                    return result;
                }
            },

            legend: {
                bottom: 0
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: sbd.periodi
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Volume (litri)',
                    axisLabel: {
                        formatter: (val) => formatCompact(val)
                    }
                },
                {
                    type: 'value',
                    name: 'Costo (€)',
                    axisLabel: {
                        formatter: (val) => formatEuro(val)
                    }
                }
            ],
            series: [...barSeries, lineSeries]
        };
    };

    // CONFIGURAZIONE COMBO PER TARGHE
    const getTargaChartOption = () => {
        const isSource = chartFilter?.type === 'targa';
        const source = isSource ? data : (filteredData || data);

        if (!source?.perTargaData) return {};

        const ptd = source.perTargaData;

        return {
            tooltip: {
                trigger: 'axis'
            },
            legend: {
                top: 0,
                left: 0
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '8%',
                containLabel: true
            },
            dataZoom: [
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    start: 0,
                    end: ptd.targhe.length > 10 ? Math.round((10 / ptd.targhe.length) * 100) : 100,
                    bottom: '2%',
                    height: 20,
                }
            ],
            xAxis: {
                type: 'category',
                data: ptd.targhe,
                axisLabel: {
                    rotate: 30,
                    fontSize: 10
                }
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Costo / Volume',
                    axisLabel: {
                        formatter: (val) => formatCompact(val)
                    }
                },
                {
                    type: 'value',
                    name: '€/litro',
                    axisLabel: {
                        formatter: (val) => formatNumber(val) + ' €'
                    }
                }
            ],
            series: [
                {
                    name: 'Costo Totale',
                    type: 'bar',
                    barGap: 0,
                    z: 10,
                    data: ptd.costoTotale.map((val, i) => ({
                        value: val,
                        itemStyle: isSource && ptd.targhe[i] !== chartFilter.value
                            ? { opacity: 0.3 } : {}
                    })),
                    itemStyle: { color: '#ee6666' },
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 11,
                        fontWeight: 'bold',
                        backgroundColor: '#e9e7e7a0',
                        padding: 2,
                        borderRadius: 2,
                        formatter: (params) => formatEuro(params.value)
                    }
                },
                {
                    name: 'Volume Totale',
                    type: 'bar',
                    z: 10,
                    data: ptd.volumeTotale.map((val, i) => ({
                        value: val,
                        itemStyle: isSource && ptd.targhe[i] !== chartFilter.value
                            ? { opacity: 0.3 } : {}
                    })),
                    itemStyle: { color: '#C8EA75' },
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 11,
                        fontWeight: 'bold',
                        backgroundColor: '#e9e7e7a0',
                        padding: 2,
                        borderRadius: 2,
                        z: 10,
                        formatter: (params) => formatCompact(params.value)
                    }
                },
                {
                    name: 'Costo Medio/Litro',
                    type: 'line',
                    yAxisIndex: 1,
                    data: ptd.costoMedioLitro,
                    itemStyle: { color: '#ff6600' },
                    lineStyle: { width: 2 },
                }
            ]
        };
    };

    const renderFullscreenBtn = (isFullscreen, setIsFullscreen) => (
        <button
            className="panoramica-fullscreen-btn"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isFullscreen ? (
                    <>
                        <polyline points="4 14 10 14 10 20"></polyline>
                        <polyline points="20 10 14 10 14 4"></polyline>
                        <line x1="14" y1="10" x2="21" y2="3"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                    </>
                ) : (
                    <>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                    </>
                )}
            </svg>
        </button>
    );

    const isLoading = parentLoading || dataLoading;

    if (isLoading) {
        return (
            <Lf23Loader />
        );
    }

    if (!data) {
        return (
            <div className="panoramica-empty">
                Applica i filtri per visualizzare i dati
            </div>
        );
    }

    return (
        <div className="panoramica-container">
            <div className="panoramica-charts-row">
                <div className={`panoramica-chart-half${isFullscreenPie ? ' fullscreen-panoramica' : ''}`}>
                    <RotateDevice active={isFullscreenPie} />
                    <div className="charts-title">
                        <span>Totale Costi per Prodotto e Gestore</span>
                        {renderFullscreenBtn(isFullscreenPie, setIsFullscreenPie)}
                    </div>
                    <ReactECharts
                        option={getPieChartOption()}
                        style={{ height: '350px' }}
                        notMerge={true}
                        onEvents={{ click: handlePieClick }}
                    />
                </div>

                <div className={`panoramica-chart-half${isFullscreenLine ? ' fullscreen-panoramica' : ''}`}>
                    <RotateDevice active={isFullscreenLine} />
                    <div className="charts-title">
                        <span>Costo medio al litro per Gestore nel tempo</span>
                        {renderFullscreenBtn(isFullscreenLine, setIsFullscreenLine)}
                    </div>
                    <ReactECharts
                        option={getLineChartOption()}
                        style={{ height: '350px' }}
                        notMerge={true}
                        onEvents={{ click: handleLineClick }}
                    />
                </div>
            </div>

            <div className="panoramica-charts-row">
                <div className={`panoramica-chart-half${isFullscreenCombo ? ' fullscreen-panoramica' : ''}`}>
                    <RotateDevice active={isFullscreenCombo} />
                    <div className="panoramica-combo-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ marginBottom: "0px" }} className="charts-title">
                            Volume Totale e Costo Totale per Prodotto
                        </div>
                        <div className="oper-hr-table-view-type-container">
                            <button
                                type="button"
                                className={`oper-hr-table-view-btn ${comboViewType === 'mensile' ? 'active' : ''}`}
                                onClick={() => setComboViewType('mensile')}
                                title="Vista Mensile"
                            >
                                <span>M</span>
                            </button>
                            <button
                                type="button"
                                className={`oper-hr-table-view-btn ${comboViewType === 'trimestrale' ? 'active' : ''}`}
                                onClick={() => setComboViewType('trimestrale')}
                                title="Vista Trimestrale"
                            >
                                <span>T</span>
                            </button>
                            <button
                                type="button"
                                className={`oper-hr-table-view-btn ${comboViewType === 'annuale' ? 'active' : ''}`}
                                onClick={() => setComboViewType('annuale')}
                                title="Vista Annuale"
                            >
                                <span>A</span>
                            </button>

                            {renderFullscreenBtn(isFullscreenCombo, setIsFullscreenCombo)}

                        </div>
                    </div>
                    <ReactECharts
                        option={getStackedBarLineOption()}
                        style={{ height: '350px' }}
                        notMerge={true}
                        onEvents={{ click: handleComboClick }}
                    />
                </div>

                <div className={`panoramica-chart-half${isFullscreenTarga ? ' fullscreen-panoramica' : ''}`}>
                    <RotateDevice active={isFullscreenTarga} />
                    <div className="charts-title">
                        <span>Costo Totale, Volume Totale e Costo medio al litro per targa</span>
                        {renderFullscreenBtn(isFullscreenTarga, setIsFullscreenTarga)}
                    </div>
                    <ReactECharts
                        option={getTargaChartOption()}
                        style={{ height: '350px' }}
                        notMerge={true}
                        onEvents={{ click: handleTargaClick }}
                    />
                </div>
            </div>
        </div>
    );

};

export default Panoramica;